import { type AuditShipment, type SkuAudit, summarizeAudit } from "@shared/fba-audit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Download, RefreshCw } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertsTabs } from "@/components/AlertsTabs";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api, apiWithMeta } from "../lib/api";
import { integer, whenever } from "../lib/format";
import i18n from "../lib/i18n";

interface AuditMeta {
  refreshedAt: string | null;
  windowDays: number;
  running: { startedAt: string } | null;
  lastError: { message: string; at: string } | null;
}

/**
 * FBA stockout audit (Isaac, 2026-10-13): how often each FBA SKU sat at zero
 * sellable units over the last 100 days and how soon after that a shipment
 * was created. An objective record for reviewing restock habits -- it shows
 * WHEN shipments were made, not who made them (Amazon does not say).
 * Rebuilt weekly (Mon 04:11) and on demand; see server/lib/fba-audit.ts.
 */
export default function StockoutAudit() {
  const { t } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const qc = useQueryClient();
  const [onlyStockouts, setOnlyStockouts] = useState(true);
  const [open, setOpen] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["fba-stockout-audit"],
    queryFn: () => apiWithMeta<SkuAudit[], AuditMeta>("/api/v1/inventory/fba-audit"),
    // Poll while a rebuild is running so the page fills in by itself.
    refetchInterval: (q) => (q.state.data?.meta?.running ? 10_000 : false),
  });
  const refresh = useMutation({
    mutationFn: () => api("/api/v1/inventory/fba-audit/refresh", { method: "POST" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["fba-stockout-audit"] }),
  });

  const rows: SkuAudit[] = Array.isArray(data?.data)
    ? data.data.map((r) => ({
        ...r,
        periods: Array.isArray(r.periods) ? r.periods : [],
        shipments: Array.isArray(r.shipments) ? (r.shipments as AuditShipment[]) : [],
      }))
    : [];
  const meta = data?.meta;
  const running = Boolean(meta?.running) || refresh.isPending;
  const summary = summarizeAudit(rows);
  const shown = onlyStockouts ? rows.filter((r) => r.periods.length > 0) : rows;

  const downloadCsv = () => {
    const esc = (v: string | number) => `"${String(v).replaceAll('"', '""')}"`;
    const head = [
      "sku",
      "asin",
      "units_sold",
      "units_per_day_in_stock",
      "days_out",
      "est_units_lost",
      "stockouts",
      "stock_now",
      "shipments",
    ];
    const lines = rows.map((r) =>
      [
        r.sku,
        r.asin ?? "",
        r.unitsSold,
        r.ratePerDay,
        r.daysOut,
        r.estLostUnits,
        r.periods.map((p) => `${p.start} to ${p.end}`).join("; "),
        r.endingBalance,
        r.shipments.map((s) => `${s.createdOn}:${s.qty}`).join(" "),
      ]
        .map(esc)
        .join(","),
    );
    const blob = new Blob([[head.join(","), ...lines].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `fba-stockout-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const stat = (label: string, value: string, hint?: string) => (
    <Card key={label}>
      <CardContent className="p-4">
        <div className="text-muted-foreground text-xs">{label}</div>
        <div className="font-semibold text-2xl tabular-nums">{value}</div>
        {hint && <div className="mt-1 text-muted-foreground text-xs">{hint}</div>}
      </CardContent>
    </Card>
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      <div className="mb-2 flex flex-wrap items-center gap-3">
        <h1 className="font-semibold text-2xl tracking-tight">{t("alerts:audit.title")}</h1>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          <span className="text-muted-foreground text-sm">
            {running
              ? t("alerts:audit.running")
              : meta?.refreshedAt
                ? t("alerts:audit.updated", { when: whenever(meta.refreshedAt, locale) })
                : t("alerts:audit.never")}
          </span>
          <Button size="sm" variant="outline" onClick={downloadCsv} disabled={rows.length === 0}>
            <Download className="h-4 w-4" aria-hidden />
            CSV
          </Button>
          <Button size="sm" variant="outline" disabled={running} onClick={() => refresh.mutate()}>
            <RefreshCw className={running ? "h-4 w-4 animate-spin" : "h-4 w-4"} aria-hidden />
            {t("alerts:audit.refresh")}
          </Button>
        </div>
      </div>
      <AlertsTabs />
      <p className="mb-4 text-muted-foreground text-sm">
        {t("alerts:audit.hint", { days: meta?.windowDays ?? 100 })}
      </p>

      {meta?.lastError && (
        <Card className="mb-4 border-destructive/50">
          <CardContent className="p-4 text-sm">
            {t("alerts:audit.failed", { message: meta.lastError.message })}
          </CardContent>
        </Card>
      )}

      {isLoading ? null : rows.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-muted-foreground text-sm">
            {running ? t("alerts:audit.running") : t("alerts:audit.empty")}
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            {stat(
              t("alerts:audit.stat.withStockout"),
              `${summary.skusWithStockout} / ${summary.activeSkus}`,
              `${summary.pctWithStockout}%`,
            )}
            {stat(t("alerts:audit.stat.daysOut"), integer(summary.daysOut, locale))}
            {stat(
              t("alerts:audit.stat.lost"),
              `~${integer(summary.estLostUnits, locale)}`,
              t("alerts:audit.stat.lostHint"),
            )}
            {stat(t("alerts:audit.stat.now"), integer(summary.currentlyOut, locale))}
            {stat(
              t("alerts:audit.stat.reactive"),
              `${summary.reactivePeriods} / ${summary.stockoutPeriods}`,
              t("alerts:audit.stat.reactiveHint"),
            )}
            {stat(
              t("alerts:audit.stat.toNext"),
              summary.medianDaysToNextShipment === null
                ? "—"
                : t("alerts:audit.days", { count: summary.medianDaysToNextShipment }),
              t("alerts:audit.stat.toNextHint"),
            )}
            {stat(
              t("alerts:audit.stat.sincePrev"),
              summary.medianDaysSincePreviousShipment === null
                ? "—"
                : t("alerts:audit.days", { count: summary.medianDaysSincePreviousShipment }),
              t("alerts:audit.stat.sincePrevHint"),
            )}
            {stat(t("alerts:audit.stat.fewShipments"), String(summary.skusWithOneOrFewerShipments))}
          </div>

          <label className="mb-2 flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={onlyStockouts}
              onChange={(e) => setOnlyStockouts(e.target.checked)}
            />
            {t("alerts:audit.onlyStockouts")}
          </label>

          <Card>
            <CardContent className="overflow-x-auto p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground text-xs">
                    <th className="p-3" />
                    <th className="p-3">SKU</th>
                    <th className="p-3 text-right">{t("alerts:audit.col.perDay")}</th>
                    <th className="p-3 text-right">{t("alerts:audit.col.daysOut")}</th>
                    <th className="p-3 text-right">{t("alerts:audit.col.lost")}</th>
                    <th className="p-3 text-right">{t("alerts:audit.col.now")}</th>
                    <th className="p-3 text-right">{t("alerts:audit.col.shipments")}</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => (
                    <AuditRow
                      key={r.sku}
                      r={r}
                      expanded={open === r.sku}
                      onToggle={() => setOpen(open === r.sku ? null : r.sku)}
                    />
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function AuditRow({
  r,
  expanded,
  onToggle,
}: {
  r: SkuAudit;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation();
  const Chevron = expanded ? ChevronDown : ChevronRight;
  return (
    <>
      <tr className="cursor-pointer border-b hover:bg-muted/40" onClick={onToggle}>
        <td className="p-3">
          <Chevron className="h-4 w-4" aria-hidden />
        </td>
        <td className="p-3">
          <div className="font-medium">{r.sku}</div>
          <div className="max-w-md truncate text-muted-foreground text-xs">
            {r.asin} {r.title}
          </div>
        </td>
        <td className="p-3 text-right tabular-nums">{r.ratePerDay}</td>
        <td className="p-3 text-right tabular-nums">{r.daysOut}</td>
        <td className="p-3 text-right tabular-nums">{r.estLostUnits}</td>
        <td className="p-3 text-right tabular-nums">{r.endingBalance}</td>
        <td className="p-3 text-right tabular-nums">{r.shipments.length}</td>
      </tr>
      {expanded && (
        <tr className="border-b bg-muted/20">
          <td />
          <td colSpan={6} className="space-y-3 p-3">
            <div>
              <div className="mb-1 font-medium text-xs">{t("alerts:audit.stockouts")}</div>
              {r.periods.length === 0 ? (
                <div className="text-muted-foreground text-xs">{t("alerts:audit.none")}</div>
              ) : (
                <ul className="space-y-1 text-xs">
                  {r.periods.map((p) => (
                    <li key={p.start}>
                      {p.start} → {p.end} ({t("alerts:audit.days", { count: p.days })}) ·{" "}
                      {p.previousShipment
                        ? t("alerts:audit.prev", {
                            qty: p.previousShipment.qty,
                            days: p.previousShipment.daysBefore,
                          })
                        : t("alerts:audit.noPrev")}{" "}
                      ·{" "}
                      {p.nextShipmentAfterDays === null
                        ? t("alerts:audit.noNext")
                        : t("alerts:audit.next", { days: p.nextShipmentAfterDays })}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <div className="mb-1 font-medium text-xs">{t("alerts:audit.shipmentsMade")}</div>
              {r.shipments.length === 0 ? (
                <div className="text-muted-foreground text-xs">{t("alerts:audit.none")}</div>
              ) : (
                <ul className="space-y-1 text-xs">
                  {r.shipments.map((s) => (
                    <li key={s.id}>
                      {s.createdOn} · {s.qty} {t("alerts:audit.units")} · {s.status} · {s.id}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
